import axiosInstance from "@frontend/app/api/axiosInstance.ts";
import { DocComment } from "@mjjs/ui";
import _ from "lodash";

// Types
type Company = string;
type Position = string;
type RequirementsComparison = object;

export interface JobPostCaterUpdateInputData {
  company: Company;
  position: Position;
  hiringManagerName?: string | null;
  requirementsComparison: RequirementsComparison;
}

export interface JobPostCaterInputData extends JobPostCaterUpdateInputData {
  comments: Record<string, DocComment>;
}

type CreateJobPostCaterInputData = Pick<JobPostCaterInputData, "company" | "position">;

// Common fields to retrieve in each query/mutation
const allFields = `
  _id
  company
  position
  hiringManagerName
  requirementsComparison
  comments
  createdAt
  updatedAt
`;

const createFields = `
  _id
  company
  position
  requirementsComparison
  createdAt
  updatedAt
`;

const QUERIES = {
  GET: `
    query GetJobPostCater($id: String!) {
      jobPostCater(id: $id) {
        ${allFields}
      }
    }
  `,
  UPDATE: `
    mutation UpdateJobPostCater($id: String!, $input: JobPostCaterUpdateInput!) {
      updateJobPostCater(id: $id, input: $input) {
        ${allFields}
      }
    }
  `,
  LIST: `
    query {
      jobPostCaters {
        ${allFields}
      }
    }
  `,
  CREATE: `
    mutation CreateJobPostCater($input: NewJobPostCaterInput!) {
      createJobPostCater(input: $input) {
        ${createFields}
      }
    }
  `,
  DELETE: `
    mutation DeleteJobPostCater($id: String!) {
      deleteJobPostCater(id: $id) {
        _id
      }
    }
  `,
  REQUEST_COMMENT_IMAGE_URL: `
    mutation RequestCommentImageUploadUrl($filename: String!, $filetype: String!) {
      requestCommentImageUploadUrl(filename: $filename, filetype: $filetype) {
        uploadUrl
        key
      }
    }
  `,
  ADD_COMMENT: `
    mutation AddCommentToJobPostCater($caterID: String!, $comment: JobCaterCommentInput!, $imageKey: String) {
      addCommentToJobPostCater(caterID: $caterID, comment: $comment, imageKey: $imageKey) {
        ${allFields}
      }
    }
  `,
  DELETE_COMMENT: `
    mutation DeleteCommentFromJobPostCater($caterID: String!, $commentID: String!) {
      deleteCommentFromJobPostCater(caterID: $caterID, commentID: $commentID) {
        ${allFields}
      }
    }
  `,
  UPDATE_COMMENT: `
    mutation UpdateCommentFromJobPostCater($caterID: String!, $commentID: String!, $comment: JobCaterCommentInput!) {
      updateCommentFromJobPostCater(caterID: $caterID, commentID: $commentID, comment: $comment) {
        ${allFields}
      }
    }
  `,
};

async function postGraphQL<T = any>(
  query: string,
  variables: Record<string, any> = {}
): Promise<T> {
  const res = await axiosInstance.post("/graphql", { query, variables });
  return res.data.data;
}

const JobPostCaterAPI = {
  get: async (id: string) => {
    const data = await postGraphQL<{ jobPostCater: any }>(QUERIES.GET, { id });
    return data.jobPostCater;
  },

  update: async (id: string, input: JobPostCaterUpdateInputData) => {
    const data = await postGraphQL<{ updateJobPostCater: any }>(QUERIES.UPDATE, { id, input });
    return data.updateJobPostCater;
  },

  list: async () => {
    const data = await postGraphQL<{ jobPostCaters: any[] }>(QUERIES.LIST);
    return data.jobPostCaters;
  },

  create: async (input: CreateJobPostCaterInputData) => {
    const data = await postGraphQL<{ createJobPostCater: any }>(QUERIES.CREATE, { input });
    return data.createJobPostCater;
  },

  delete: async (id: string) => {
    const data = await postGraphQL<{ deleteJobPostCater: any }>(QUERIES.DELETE, { id });
    return data.deleteJobPostCater;
  },

  addComment: async (caterID: string, comment: DocComment & { id: string }, image?: File) => {
    let imageKey: string | undefined;

    if (image) {
      const { requestCommentImageUploadUrl } = await postGraphQL<{
        requestCommentImageUploadUrl: { uploadUrl: string; key: string };
      }>(QUERIES.REQUEST_COMMENT_IMAGE_URL, {
        filename: image.name,
        filetype: image.type,
      });

      await fetch(requestCommentImageUploadUrl.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": image.type },
        body: image,
      });

      imageKey = requestCommentImageUploadUrl.key;
    }

    const data = await postGraphQL<{ addCommentToJobPostCater: any }>(QUERIES.ADD_COMMENT, {
      caterID,
      comment: _.pick(comment, ["id", "text", "imageURL"]),
      imageKey,
    });

    return data.addCommentToJobPostCater;
  },

  deleteComment: async (caterID: string, commentID: string) => {
    const data = await postGraphQL<{ deleteCommentFromJobPostCater: any }>(QUERIES.DELETE_COMMENT, {
      caterID,
      commentID,
    });

    return data.deleteCommentFromJobPostCater;
  },
  updateComment: async (caterID: string, commentID: string, comment: DocComment) => {
    const data = await postGraphQL<{ updateCommentFromJobPostCater: any }>(QUERIES.UPDATE_COMMENT, {
      caterID,
      commentID,
      comment: _.pick(comment, ["id", "text", "imageURL"]),
    });

    return data.updateCommentFromJobPostCater;
  },
};

export default JobPostCaterAPI;
